import {Moment} from 'moment';
import {IFormController, ILogService} from 'angular';
import {ITranslateService} from 'angular-translate';
import {IJournalDailyDataRow} from './journal.controller';
import {IDataValueApi} from '../../api/data-value-api.service';
import {ISnackbarService} from '../../snackbar/snackbar.service';
import {IDailyTriplet} from '../../api/api.types';

const editDialogTemplate = require<string>('./edit-daily-data-row-dialog.html');

interface IJournalMinMaxDataRow {
  waterLevelMorning: number;
  dischargeMorning: number;
  waterLevelEvening: number;
  dischargeEvening: number;
  waterLevelAverage: number;
  dischargeAverage: number;
  waterTemperature: number;
  airTemperature: number;
  icePhenomena: number;
}

export default class DailyDataTableController {
  static readonly $inject = ['$mdDialog', '$translate',
    '$log', 'snackbarService'];

  /**
   * ID of the station related to the data.
   */
  stationId: number;
  /**
   * Daily data to display in the table.
   */
  dailyData: IJournalDailyDataRow[]|null;
  /**
   * Minimum values found in the daily data.
   */
  minimumData: IJournalMinMaxDataRow|null;
  /**
   * Maximum values derived from the daily data.
   */
  maximumData: IJournalMinMaxDataRow|null;
  /**
   * Selected daily data rows.
   */
  selectedRows: IJournalDailyDataRow[];

  isVirtualStation: boolean;

  loadJournal: () => void;

  constructor(private $mdDialog: ng.material.IDialogService,
              private $translate: ITranslateService,
              private $log: ILogService,
              private snackbarService: ISnackbarService) {
    this.selectedRows = [];
  }

  /**
   * Selected row, if any.
   */
  get selectedRow(): IJournalDailyDataRow|null {

    return this.selectedRows.length > 0 ? this.selectedRows[0] : null;
  }

  $onChanges() {
    this.dailyData = this.dailyData || [];
    if (this.dailyData.length == 0) {
      this.minimumData = null;
      this.maximumData = null;
    }
    this.calculateMinimumMaximumValues();
  }

  editRow($event: MouseEvent) {
    const dialogOptions: ng.material.IDialogOptions = {
      controller: EditDailyDataRowDialogController,
      controllerAs: '$ctrl',
      escapeToClose: false,
      locals: {
        dataRow: this.selectedRow,
        stationId: this.stationId
      },
      targetEvent: $event,
      template: editDialogTemplate
    };
    this.$mdDialog.show(dialogOptions)
        .then((dailyTriplet:IDailyTriplet) => {
          if (dailyTriplet.eightData) {
            this.selectedRow.waterLevelMorning = dailyTriplet.eightData
                .waterLevel.dataValue;
            this.selectedRow.dischargeMorning = dailyTriplet.eightData
                .discharge.dataValue;
          }
          if (dailyTriplet.twentyData) {
            this.selectedRow.waterLevelEvening = dailyTriplet.twentyData
                .waterLevel.dataValue;
            this.selectedRow.dischargeEvening = dailyTriplet.twentyData
                .discharge.dataValue;
          }
          if (dailyTriplet.averageData) {
            this.selectedRow.waterLevelAverage = dailyTriplet.averageData
                .waterLevel.dataValue;
            this.selectedRow.dischargeAverage = dailyTriplet.averageData
                .discharge.dataValue;
          }
          this.calculateMinimumMaximumValues();
          this.snackbarService.show({
            content: this.$translate.instant(
                'DAILY_DATA_WAS_EDITED')
          });

          this.loadJournal();
        })
        .catch(error => {
          if (error) {
            this.snackbarService.show({
              content: this.$translate.instant(
                  'ERROR_UPDATING_DATA')
            });
            this.$log.error(error);
          }
        });
  }

  private calculateMinimumMaximumValues() {
    this.minimumData = {
      waterLevelMorning: Number.MAX_VALUE,
      dischargeMorning: Number.MAX_VALUE,
      waterLevelEvening: Number.MAX_VALUE,
      dischargeEvening: Number.MAX_VALUE,
      waterLevelAverage: Number.MAX_VALUE,
      dischargeAverage: Number.MAX_VALUE,
      waterTemperature: Number.MAX_VALUE,
      airTemperature: Number.MAX_VALUE,
      icePhenomena: Number.MAX_VALUE,
    };
    this.maximumData = {
      waterLevelMorning: Number.MIN_VALUE,
      dischargeMorning: Number.MIN_VALUE,
      waterLevelEvening: Number.MIN_VALUE,
      dischargeEvening: Number.MIN_VALUE,
      waterLevelAverage: Number.MIN_VALUE,
      dischargeAverage: Number.MIN_VALUE,
      waterTemperature: Number.NEGATIVE_INFINITY,
      airTemperature: Number.NEGATIVE_INFINITY,
      icePhenomena: Number.MIN_VALUE,
    };

    this.dailyData.forEach(dailyDatum => {
      if (dailyDatum.waterLevelMorning != null) {
        this.minimumData.waterLevelMorning = Math.min(
            dailyDatum.waterLevelMorning, this.minimumData.waterLevelMorning);
        this.maximumData.waterLevelMorning = Math.max(
            dailyDatum.waterLevelMorning, this.maximumData.waterLevelMorning);
      }
      if (dailyDatum.dischargeMorning != null) {
        this.minimumData.dischargeMorning = Math.min(
            dailyDatum.dischargeMorning, this.minimumData.dischargeMorning);
        this.maximumData.dischargeMorning = Math.max(
            dailyDatum.dischargeMorning, this.maximumData.dischargeMorning);
      }
      if (dailyDatum.waterLevelEvening != null) {
        this.minimumData.waterLevelEvening = Math.min(
            dailyDatum.waterLevelEvening, this.minimumData.waterLevelEvening);
        this.maximumData.waterLevelEvening = Math.max(
            dailyDatum.waterLevelEvening, this.maximumData.waterLevelEvening);
      }
      if (dailyDatum.dischargeEvening != null) {
        this.minimumData.dischargeEvening = Math.min(
            dailyDatum.dischargeEvening, this.minimumData.dischargeEvening);
        this.maximumData.dischargeEvening = Math.max(
            dailyDatum.dischargeEvening, this.maximumData.dischargeEvening);
      }
      if (dailyDatum.waterLevelAverage != null) {
        this.minimumData.waterLevelAverage = Math.min(
            dailyDatum.waterLevelAverage, this.minimumData.waterLevelAverage);
        this.maximumData.waterLevelAverage = Math.max(
            dailyDatum.waterLevelAverage, this.maximumData.waterLevelAverage);
      }
      if (dailyDatum.dischargeAverage != null) {
        this.minimumData.dischargeAverage = Math.min(
            dailyDatum.dischargeAverage, this.minimumData.dischargeAverage);
        this.maximumData.dischargeAverage = Math.max(
            dailyDatum.dischargeAverage, this.maximumData.dischargeAverage);
      }
      if (dailyDatum.waterTemperature != null) {
          this.minimumData.waterTemperature = Math.min(
              dailyDatum.waterTemperature, this.minimumData.waterTemperature);
          this.maximumData.waterTemperature = Math.max(
              dailyDatum.waterTemperature, this.maximumData.waterTemperature);
      }
      if (dailyDatum.airTemperature != null) {
          this.minimumData.airTemperature = Math.min(
              dailyDatum.airTemperature, this.minimumData.airTemperature);
          this.maximumData.airTemperature = Math.max(
              dailyDatum.airTemperature, this.maximumData.airTemperature);
      }
      // if (dailyDatum.icePhenomena != null) {
      //     this.minimumData.icePhenomena = Math.min(
      //         dailyDatum.icePhenomena, this.minimumData.icePhenomena);
      //     this.maximumData.icePhenomena = Math.max(
      //         dailyDatum.icePhenomena, this.maximumData.icePhenomena);
      // }
    });

    this.minimumData = {
      waterLevelMorning: this.minimumData.waterLevelMorning == Number.MAX_VALUE
          ? Number.NaN : this.minimumData.waterLevelMorning,
      dischargeMorning: this.minimumData.dischargeMorning == Number.MAX_VALUE
          ? Number.NaN : this.minimumData.dischargeMorning,
      waterLevelEvening: this.minimumData.waterLevelEvening == Number.MAX_VALUE
          ? Number.NaN : this.minimumData.waterLevelEvening,
      dischargeEvening: this.minimumData.dischargeEvening == Number.MAX_VALUE
          ? Number.NaN : this.minimumData.dischargeEvening,
      waterLevelAverage: this.minimumData.waterLevelAverage == Number.MAX_VALUE
          ? Number.NaN : this.minimumData.waterLevelAverage,
      dischargeAverage: this.minimumData.dischargeAverage == Number.MAX_VALUE
          ? Number.NaN : this.minimumData.dischargeAverage,
      waterTemperature: this.minimumData.waterTemperature == Number.MAX_VALUE
          ? Number.NaN : this.minimumData.waterTemperature,
      airTemperature: this.minimumData.airTemperature == Number.MAX_VALUE
          ? Number.NaN : this.minimumData.airTemperature,
      icePhenomena: this.minimumData.icePhenomena == Number.MAX_VALUE
          ? Number.NaN : this.minimumData.icePhenomena
    };

    this.maximumData = {
      waterLevelMorning: this.maximumData.waterLevelMorning == Number.MIN_VALUE
          ? Number.NaN : this.maximumData.waterLevelMorning,
      dischargeMorning: this.maximumData.dischargeMorning == Number.MIN_VALUE
          ? Number.NaN : this.maximumData.dischargeMorning,
      waterLevelEvening: this.maximumData.waterLevelEvening == Number.MIN_VALUE
          ? Number.NaN : this.maximumData.waterLevelEvening,
      dischargeEvening: this.maximumData.dischargeEvening == Number.MIN_VALUE
          ? Number.NaN : this.maximumData.dischargeEvening,
      waterLevelAverage: this.maximumData.waterLevelAverage == Number.MIN_VALUE
          ? Number.NaN : this.maximumData.waterLevelAverage,
      dischargeAverage: this.maximumData.dischargeAverage == Number.MIN_VALUE
          ? Number.NaN : this.maximumData.dischargeAverage,
      waterTemperature: this.maximumData.waterTemperature == Number.NEGATIVE_INFINITY
          ? Number.NaN : this.maximumData.waterTemperature,
      airTemperature: this.maximumData.airTemperature == Number.NEGATIVE_INFINITY
          ? Number.NaN : this.maximumData.airTemperature,
      icePhenomena: this.maximumData.icePhenomena == Number.MIN_VALUE
          ? Number.NaN : this.maximumData.icePhenomena
    };
  }
}

class EditDailyDataRowDialogController {
  static readonly $inject = ['dataRow', 'stationId',
    '$mdDialog', 'dataValueApi'];

  /**
   * Edit form controller.
   */
  editForm: IFormController
  /**
   * Data row's overriddenDate.
   */
  date: Moment;
  /**
   * Editable morning water level.
   */
  waterLevelMorning: number;
  /**
   * Editable evening water level.
   */
  waterLevelEvening: number;
  /**
   * Whether a loading operation is in progress.
   */
  loadingInProgress: boolean;

  constructor(private dataRow: IJournalDailyDataRow,
              private stationId: number,
              private $mdDialog: ng.material.IDialogService,
              private dataValueApi: IDataValueApi) {
    this.date = dataRow.date;
    this.waterLevelMorning = dataRow.waterLevelMorning;
    this.waterLevelEvening = dataRow.waterLevelEvening;
    this.loadingInProgress = false;
  }

  /**
   * Whether the apply changes button is disabled.
   */
  get disabledApplyButton(): boolean {
    return !this.editForm || this.editForm.$invalid || this.loadingInProgress;
  }

  /**
   * Calculated water level average.
   */
  get waterLevelAverage(): number {
    if (this.dataRow.waterLevelAverage == null) {
      return null;
    }
    let waterLevelSum = 0;
    let waterLevelCount = 0;
    if (this.waterLevelMorning != null) {
      waterLevelSum += this.waterLevelMorning;
      waterLevelCount++;
    }
    if (this.waterLevelEvening != null) {
      waterLevelSum += this.waterLevelEvening;
      waterLevelCount++;
    }
    return waterLevelCount > 1
        ? Math.round(waterLevelSum / waterLevelCount)
        : Number.NaN;
  }

  /**
   * Apply the changes to the daily water level.
   */
  applyChanges() {
    this.dataValueApi.editDailyData(
        this.stationId, this.date, this.waterLevelMorning,
        this.waterLevelEvening, this.waterLevelAverage)
        .then(dailyTriplet => {
          this.$mdDialog.hide(dailyTriplet);
        })
        .catch(error => {
          this.$mdDialog.cancel(error);
        });
  }

  /**
   * Cancel the dialog without any changes.
   */
  cancel() {
    this.$mdDialog.cancel();
  }
}



/** WEBPACK FOOTER **
 ** ./app/operational/journal/daily-data-table.controller.ts
 **/